import React, { useState, useEffect, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronRight, ChevronLeft } from 'lucide-react';
import { useApp } from '../context/AppContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Memoized video card component for better performance
const VideoCard = memo(({ video, onClick }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className="bg-white rounded-3xl overflow-hidden shadow-lg cursor-pointer"
    data-testid={`video-card-${video.video_id}`}
  >
    <img
      src={video.thumbnail}
      alt={video.title}
      className="w-full aspect-video object-cover"
      loading="lazy"
    />
    <div className="p-4">
      <h3 className="font-bold text-lg text-gray-800 line-clamp-2 fredoka">
        {video.title}
      </h3>
    </div>
  </motion.div>
));

VideoCard.displayName = 'VideoCard';

const KidMode = ({ onBack }) => {
  const { t } = useApp();
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [timeLimit, setTimeLimit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    loadChannels();
    checkTimeLimit();
  }, []);

  useEffect(() => {
    if (selectedVideo) {
      setStartTime(Date.now());
    }
  }, [selectedVideo]);

  const loadChannels = async () => {
    try {
      const response = await axios.get(`${API}/channels`);
      setChannels(response.data);
    } catch (error) {
      console.error('Error loading channels:', error);
    }
  };

  const checkTimeLimit = async () => {
    try {
      const response = await axios.get(`${API}/time-limits/status`);
      setTimeLimit(response.data);
    } catch (error) {
      console.error('Error checking time limit:', error);
    }
  };

  const loadVideos = async (channelId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/channels/${channelId}/videos`);
      setVideos(response.data.videos);
      setSelectedChannel(channelId);
      setLoading(false);
    } catch (error) {
      console.error('Error loading videos:', error);
      alert('Could not load videos. Please try again.');
      setLoading(false);
    }
  };

  const handleVideoEnd = async () => {
    if (startTime && selectedVideo) {
      const durationSeconds = Math.floor((Date.now() - startTime) / 1000);
      try {
        await axios.post(`${API}/watch-history`, {
          video_id: selectedVideo.video_id,
          video_title: selectedVideo.title,
          channel_title: selectedVideo.channel_title,
          duration_watched_seconds: durationSeconds
        });
        checkTimeLimit();
      } catch (error) {
        console.error('Error saving watch history:', error);
      }
    }
    setSelectedVideo(null);
    setStartTime(null);
  };

  if (timeLimit && !timeLimit.can_watch) {
    return (
      <div className="min-h-screen bg-[#FFFDF5] flex items-center justify-center p-8" data-testid="time-limit-screen">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="text-8xl mb-6">⏰</div>
          <h1 className="text-4xl font-bold text-[#2D3748] mb-4 fredoka">
            {t('timeLimitReached')}
          </h1>
          <p className="text-xl text-gray-600 mb-8">{t('watchAgainTomorrow')}</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-800 font-bold text-2xl px-12 py-6 rounded-full shadow-lg fredoka"
            data-testid="back-home-button"
          >
            {t('backToHome')}
          </motion.button>
        </motion.div>
      </div>
    );
  }

  if (selectedVideo) {
    const currentIndex = videos.findIndex(v => v.video_id === selectedVideo.video_id);
    const hasMoreVideos = videos.length > 1;

    return (
      <div className="fixed inset-0 bg-black z-50 flex" data-testid="video-player-screen">
        {/* Video Player - Left/Full Side */}
        <div className="flex-1 flex flex-col relative">
          <div className="flex-1 flex items-center justify-center relative">
            <iframe
              ref={iframeRef}
              src={`https://www.youtube-nocookie.com/embed/${selectedVideo.video_id}?autoplay=1&modestbranding=1&controls=1&fs=0&rel=0&disablekb=1&cc_load_policy=0&iv_load_policy=3&playsinline=1`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              title={selectedVideo.title}
              data-testid="youtube-iframe"
              style={{ border: 'none' }}
              sandbox="allow-scripts allow-same-origin"
            />
            
            {/* Toggle Button */}
            {hasMoreVideos && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowPlaylist(!showPlaylist)}
                className="absolute top-4 right-4 bg-gray-800/80 hover:bg-gray-700 text-white p-3 rounded-full shadow-lg z-10"
                data-testid="toggle-playlist-button"
              >
                {showPlaylist ? <ChevronRight size={24} /> : <ChevronLeft size={24} />}
              </motion.button>
            )}
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleVideoEnd}
            className="m-4 bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-bold text-xl px-8 py-4 rounded-full shadow-lg fredoka self-center"
            data-testid="close-video-button"
          >
            <ArrowLeft className="inline mr-2" />
            {t('backToChannels')}
          </motion.button>
        </div>

        {/* Video List - Right Side (Collapsible) */}
        <AnimatePresence>
          {hasMoreVideos && showPlaylist && (
            <motion.div
              initial={{ x: 384, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 384, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              className="w-96 bg-gray-900 overflow-y-auto p-4"
              data-testid="video-playlist"
            >
              <h3 className="text-white font-bold text-lg mb-4 fredoka">More Videos</h3>
              <div className="space-y-3">
                {videos.map((video, index) => (
                  <motion.div
                    key={video.video_id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedVideo(video)}
                    className={`cursor-pointer rounded-lg overflow-hidden ${
                      video.video_id === selectedVideo.video_id
                        ? 'ring-2 ring-[#FFD700]'
                        : 'opacity-80 hover:opacity-100'
                    }`}
                    data-testid={`playlist-video-${video.video_id}`}
                  >
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full aspect-video object-cover"
                      loading="lazy"
                    />
                    <div className="bg-gray-800 p-2">
                      <p className="text-white text-sm line-clamp-2 fredoka">
                        {video.title}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (selectedChannel) {
    return (
      <div className="min-h-screen bg-[#FFFDF5] p-8" data-testid="video-list-screen">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setSelectedChannel(null);
            setVideos([]);
          }}
          className="mb-8 bg-[#4ECDC4] hover:bg-[#3DB8AF] text-white font-bold text-xl px-8 py-4 rounded-full shadow-lg fredoka"
          data-testid="back-to-channels-button"
        >
          <ArrowLeft className="inline mr-2" />
          {t('backToChannels')}
        </motion.button>

        {loading ? (
          <div className="text-center text-2xl text-gray-600">Loading videos...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {videos.map((video) => (
              <VideoCard
                key={video.video_id}
                video={video}
                onClick={() => setSelectedVideo(video)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (channels.length === 0) {
    return (
      <div className="min-h-screen bg-[#FFFDF5] flex items-center justify-center p-8" data-testid="no-channels-screen">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="text-8xl mb-6">📺</div>
          <h1 className="text-4xl font-bold text-[#2D3748] mb-4 fredoka">
            {t('noChannels')}
          </h1>
          <p className="text-xl text-gray-600 mb-8">{t('addChannelsFirst')}</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-800 font-bold text-2xl px-12 py-6 rounded-full shadow-lg fredoka"
            data-testid="back-home-button"
          >
            {t('backToHome')}
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF5] p-8" data-testid="channel-selection-screen">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onBack}
        className="mb-8 bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-bold text-xl px-8 py-4 rounded-full shadow-lg fredoka"
        data-testid="back-home-button"
      >
        <ArrowLeft className="inline mr-2" />
        {t('backToHome')}
      </motion.button>

      <h1 className="text-5xl font-bold text-center text-[#2D3748] mb-12 fredoka">
        {t('selectChannel')}
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
        {channels.map((channel) => (
          <motion.div
            key={channel.id}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => loadVideos(channel.channel_id)}
            className="flex flex-col items-center cursor-pointer"
            data-testid={`channel-card-${channel.channel_id}`}
          >
            <div className="w-32 h-32 rounded-full overflow-hidden shadow-xl mb-4 bg-white">
              <img
                src={channel.thumbnail}
                alt={channel.title}
                className="w-full h-full object-cover"
              />
            </div>
            <h3 className="font-bold text-xl text-center text-gray-800 fredoka">
              {channel.title}
            </h3>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default KidMode;
